import { useEffect, useRef, useState } from 'react';
import { requestRemoteState } from '../Controls/remoteStateApi';
import { nativeScreenshotFailure } from '../../utils/nativeScreenshotSupport';

const INTERVAL_MS = 15000;
const GAP_MS = 2000;
const isImage = (value) => /^data:image\/(png|jpeg|webp);base64,/i.test(value || '');

export default function useScreenPreviews({ entries, scopeKey, enabled, ready, requestState = requestRemoteState }) {
  const latest = useRef(null);
  const fences = useRef(new Map());
  const pending = useRef(false);
  const attempts = useRef(new Map());
  const cooldown = useRef(0);
  const [results, setResults] = useState({});
  // Fence by identity as well as key: A → B → A must not revive the first A's reply.
  const nextFences = new Map(entries.map((entry) => {
    const previous = fences.current.get(entry.screenId);
    return [entry.screenId, previous?.scopeKey === scopeKey && previous.key === entry.target.key
      && previous.wantsSnapshot === entry.wantsSnapshot ? previous
        : { scopeKey, key: entry.target.key, wantsSnapshot: entry.wantsSnapshot }];
  }));
  fences.current = nextFences;
  latest.current = { entries, scopeKey, enabled, ready, requestState };

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    let wasReady = false;
    const valid = (id, fence) => active && latest.current.enabled && latest.current.scopeKey === scopeKey
      && fences.current.get(id) === fence;
    const publish = (members, update) => {
      if (!active) return;
      setResults((previous) => {
        const next = {};
        // Keep only current screens/contexts; image data never accumulates in a history/cache.
        latest.current.entries.forEach((entry) => {
          if (previous[entry.screenId]?.fence === fences.current.get(entry.screenId)) next[entry.screenId] = previous[entry.screenId];
        });
        members.forEach(([id, fence]) => {
          if (valid(id, fence)) next[id] = { ...next[id], ...update, fence };
        });
        return next;
      });
    };
    const tick = async () => {
      const current = latest.current;
      if (!active || !current.enabled || !current.ready || document.visibilityState === 'hidden') {
        wasReady = false;
        return;
      }
      const now = Date.now();
      if (!wasReady) { wasReady = true; cooldown.current = Math.max(cooldown.current, now + GAP_MS); }
      if (pending.current || now < cooldown.current) return;
      const candidates = current.entries.filter((entry) => {
        if (!entry.wantsSnapshot || entry.target.reason || !entry.target.deviceId) return false;
        const attempt = attempts.current.get(entry.target.deviceId);
        const same = attempt?.capabilityKey === entry.target.capabilityKey;
        return (!same || !attempt.unsupported) && now >= Math.max(
          attempt ? attempt.lastAttemptAt + INTERVAL_MS : 0, same ? attempt.nextAttemptAt : 0
        );
      }).sort((a, b) => (attempts.current.get(a.target.deviceId)?.lastAttemptAt || 0)
        - (attempts.current.get(b.target.deviceId)?.lastAttemptAt || 0));
      const entry = candidates[0];
      if (!entry) return;
      const target = entry.target;
      const previous = attempts.current.get(target.deviceId);
      const attempt = { capabilityKey: target.capabilityKey, lastAttemptAt: now,
        failures: previous?.capabilityKey === target.capabilityKey ? previous.failures : 0, nextAttemptAt: now + INTERVAL_MS };
      attempts.current.delete(target.deviceId);
      attempts.current.set(target.deviceId, attempt);
      const currentIds = new Set(current.entries.map((item) => item.target.deviceId));
      // Retain every configured target for fair rotation, plus a bounded recent history.
      for (const id of attempts.current.keys()) {
        if (attempts.current.size <= currentIds.size + 64) break;
        if (!currentIds.has(id)) attempts.current.delete(id);
      }
      const members = current.entries.filter((candidate) => candidate.wantsSnapshot
        && candidate.target.deviceId === target.deviceId && candidate.target.capabilityKey === target.capabilityKey
        && candidate.target.appPackageName === target.appPackageName)
        .map((candidate) => [candidate.screenId, fences.current.get(candidate.screenId)]);
      pending.current = true;
      publish(members, { capturing: true, message: '' });
      try {
        const state = await current.requestState({ deviceId: target.deviceId, includeScreenshot: true,
          allowAlternateRoute: false, timeout: 8000 });
        if (!isImage(state?.screenshot?.dataUrl)) throw state || new Error('missing_image');
        if (target.appPackageName && state.packageName && target.appPackageName !== state.packageName) {
          throw new Error('source_changed_during_capture');
        }
        attempt.failures = 0;
        const capturedAt = Number(state.screenshot.capturedAt);
        publish(members, { dataUrl: state.screenshot.dataUrl, capturedAt: capturedAt > 0 && capturedAt <= Date.now() + 2000 ? capturedAt : null,
          receivedAt: Date.now(), capturing: false, message: '' });
      } catch (error) {
        const failure = nativeScreenshotFailure(error);
        attempt.unsupported = failure.state === 'unsupported';
        attempt.failures += 1;
        attempt.nextAttemptAt = Date.now() + Math.min(300000, 30000 * (2 ** Math.min(attempt.failures - 1, 4)));
        publish(members, { capturing: false, message: failure.reason });
      } finally {
        pending.current = false;
        // Slow captures never create a catch-up queue; the next screen also gets a quiet gap.
        cooldown.current = Date.now() + GAP_MS;
      }
    };
    const timer = window.setInterval(tick, 250);
    document.addEventListener('visibilitychange', tick);
    tick();
    return () => { active = false; window.clearInterval(timer); document.removeEventListener('visibilitychange', tick); };
  }, [enabled, scopeKey]);

  return Object.fromEntries(entries.map((entry) => [entry.screenId,
    results[entry.screenId]?.fence === fences.current.get(entry.screenId) ? results[entry.screenId] : null]));
}
